/** "What happened" — the lead's timeline: activities, closed tasks, folded
 *  unanswered dials, and each call recording's scorecard. */
import React, { useMemo, useState } from 'react';
import { Clock, Inbox, MapPin, StickyNote, Check, X, Repeat } from 'lucide-react';
import { Activity, Task } from '../../types';
import { WhatsAppIcon, CallIcon } from '../BrandIcons';
import { at, buildTimeline, callFacts, closureEntries, TimelineEntry } from './leadModel';

const FILTERS: [string, string, (e: TimelineEntry) => boolean][] = [
  ['all', 'All', () => true],
  ['calls', 'Calls', (e) => e.type === 'dials' || (e.type === 'single' && e.activity.kind === 'call')],
  ['whatsapp', 'WhatsApp', (e) => e.type === 'single' && e.activity.channel === 'whatsapp' && e.activity.kind !== 'call'],
  ['notes', 'Notes', (e) => e.type === 'single' && e.activity.kind === 'note'],
  ['tasks', 'Tasks', (e) => e.type === 'closure'],
];

export const fmtDuration = (s?: number | null) =>
  !s ? null : s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;

const clean = (s: string) =>
  s.replace(/\\u([0-9a-fA-F]{4})/g, (_, h) => String.fromCharCode(parseInt(h, 16))).replace(/<[^>]+>/g, '');

const Dot: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="w-7 h-7 rounded-full bg-cream border-[1.5px] border-sketch grid place-items-center flex-none">{children}</span>
);

const scoreChip = (n: number) => (n >= 80 ? 'chip-forest' : n >= 50 ? 'chip-gold' : 'chip-brick');

export const RecordingCard: React.FC<{ rec: Record<string, any> }> = ({ rec }) => {
  const [open, setOpen] = useState(false);
  const summary = rec.summary || {};
  const verdict = summary.verdict || {};
  const keyPoints: string[] = Array.isArray(summary.key_points) ? summary.key_points : [];
  const suggestion: string | undefined = summary.suggestion?.reason;
  const hasDetails = keyPoints.length || verdict.coaching_tip || verdict.what_went_well || suggestion;
  return (
    <div className="mt-2 bg-cream border-[1.5px] border-sketch-soft rounded-lg p-2.5">
      {rec.url && <audio controls preload="none" className="w-full h-8" src={rec.url} />}
      {rec.filename && <p className="text-[11px] text-graphite m-0 mt-1 truncate">📎 {rec.filename}</p>}
      {(verdict.call_outcome || verdict.overall_score != null || verdict.rapport != null) && (
        <div className="flex gap-1.5 flex-wrap mt-2">
          {verdict.call_outcome && <span className="chip chip-forest">{String(verdict.call_outcome).replace(/_/g, ' ')}</span>}
          {verdict.overall_score != null && <span className={`chip ${scoreChip(verdict.overall_score)}`}>Score {verdict.overall_score}/100</span>}
          {verdict.rapport != null && <span className="chip chip-mute">Rapport {verdict.rapport}</span>}
        </div>
      )}
      {summary.tl_dr && <p className="text-[12.5px] text-charcoal m-0 mt-1.5 leading-snug">{summary.tl_dr}</p>}
      {hasDetails && (
        <button type="button" className="text-forest text-xs font-semibold mt-1.5" onClick={() => setOpen(!open)}>
          {open ? 'Hide details ▲' : 'Details ▼'}
        </button>
      )}
      {open && (
        <div className="text-[12.5px] mt-1.5 space-y-1.5">
          {keyPoints.length > 0 && (
            <div><div className="card-h !mb-0.5 !text-[10.5px]">Key points</div>
              <ul className="m-0 pl-4">{keyPoints.map((k, i) => <li key={i}>{k}</li>)}</ul></div>
          )}
          {verdict.coaching_tip && <div><div className="card-h !mb-0.5 !text-[10.5px]">Coaching</div>{verdict.coaching_tip}</div>}
          {verdict.what_went_well && <div><div className="card-h !mb-0.5 !text-[10.5px]">What went well</div>{verdict.what_went_well}</div>}
          {suggestion && <div className="bg-forest/10 text-forest font-semibold rounded px-2 py-1">→ Suggested: {suggestion}</div>}
        </div>
      )}
    </div>
  );
};

const ActivityRow: React.FC<{ a: Activity; onOpenChat: () => void }> = ({ a, onOpenChat }) => {
  const p = a.payload || {};
  if (a.kind === 'note') {
    const recs: any[] = Array.isArray(p.recordings) ? p.recordings : [];
    const text = p.text || a.summary;
    return (
      <Box icon={<StickyNote className="w-3.5 h-3.5 text-graphite" strokeWidth={1.75} />}
        title={recs.length ? 'Voice note' : 'Note'} when={a.occurred_at}>
        {recs[0]?.url && <audio controls preload="none" className="w-full h-8 mt-1.5" src={recs[0].url} />}
        {text ? <p className="text-[13px] m-0 mt-1 whitespace-pre-wrap">{clean(text)}</p>
          : recs.length ? <p className="text-[13px] text-graphite italic m-0 mt-1">
            {recs[0]?.transcription?.status === 'failed' ? 'Transcription failed' : 'Transcribing…'}</p> : null}
      </Box>
    );
  }
  if (a.kind === 'call') {
    const f = callFacts(a);
    const recs = f.recordings.length ? f.recordings : f.legacyRecordingUrl ? [{ url: f.legacyRecordingUrl }] : [];
    const title = a.summary || (a.channel === 'whatsapp' ? 'WhatsApp call' : 'Call');
    return (
      <Box icon={a.channel === 'whatsapp' ? <WhatsAppIcon className="w-3.5 h-3.5" /> : <CallIcon className="w-3.5 h-3.5" />}
        title={title} when={a.occurred_at} dir={a.direction}>
        {(f.connectionResult || f.durationSeconds) && (
          <p className="text-[12.5px] text-graphite m-0 mt-1">
            {[f.connectionResult?.replace(/_/g, ' '), fmtDuration(f.durationSeconds)].filter(Boolean).join(' · ')}
          </p>
        )}
        {f.notes && <p className="text-[12.5px] italic m-0 mt-1">“{f.notes}”</p>}
        {recs.map((r, i) => <RecordingCard key={i} rec={r} />)}
      </Box>
    );
  }
  const isWa = a.channel === 'whatsapp';
  const title = a.summary || (a.kind === 'message' ? (isWa ? 'WhatsApp' : 'Message')
    : a.kind === 'visit' ? 'Site visit' : a.kind.replace(/_/g, ' '));
  const icon = isWa ? <WhatsAppIcon className="w-3.5 h-3.5" />
    : a.kind === 'visit' ? <MapPin className="w-3.5 h-3.5 text-gold" strokeWidth={1.75} />
      : a.kind === 'stage_change' || a.kind === 'assignment' ? <Repeat className="w-3.5 h-3.5 text-graphite" strokeWidth={1.75} />
        : <StickyNote className="w-3.5 h-3.5 text-graphite" strokeWidth={1.75} />;
  const clickable = a.kind === 'message' && isWa && p.via !== 'operator_personal_wa';
  return (
    <Box icon={icon} title={title} when={a.occurred_at} dir={a.direction} onClick={clickable ? onOpenChat : undefined}>
      {a.kind === 'message' && Array.isArray(p.messages) && (
        <p className="text-[12.5px] text-graphite m-0 mt-1">{p.messages.length} message{p.messages.length === 1 ? '' : 's'}</p>
      )}
    </Box>
  );
};

const Box: React.FC<{
  icon: React.ReactNode; title: string; when: string; dir?: string; onClick?: () => void; children?: React.ReactNode;
}> = ({ icon, title, when, dir, onClick, children }) => (
  <div className="grid grid-cols-[28px_minmax(0,1fr)] gap-2.5">
    <Dot>{icon}</Dot>
    <div className={`border-[1.5px] border-sketch rounded-lg bg-warm-white px-3 py-2.5 min-w-0 ${onClick ? 'cursor-pointer hover:border-forest/40' : ''}`}
      onClick={onClick}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <b className="text-[13.5px] min-w-0 break-words">{title}</b>
        <span className="text-[11.5px] text-graphite whitespace-nowrap">
          {dir && dir !== 'none' && <span className="mr-1.5">{dir}</span>}{at(when)}
        </span>
      </div>
      {children}
    </div>
  </div>
);

const ClosureRow: React.FC<{ task: Task; when: string }> = ({ task, when }) => {
  const done = task.closed_reason === 'completed';
  const verb = done ? 'done' : task.closed_reason === 'expired' ? 'expired' : 'cancelled';
  const noun = ({ call: 'Call', whatsapp: 'WhatsApp', other: 'To-do', site_visit: 'Site visit' } as Record<string, string>)[task.type] || 'Task';
  const title = task.payload?.title || task.payload?.subject;
  const cancel = task.payload?.cancel_reason;
  return (
    <Box icon={done ? <Check className="w-3.5 h-3.5 text-forest" strokeWidth={2.2} /> : <X className="w-3.5 h-3.5 text-graphite" strokeWidth={2.2} />}
      title={`${noun} ${verb}${task.source === 'selldo' ? ' · Sell.do' : ''}`} when={when}>
      {title && <p className="text-[13px] m-0 mt-1">{title}</p>}
      {cancel && <p className="text-[12.5px] italic text-graphite m-0 mt-0.5">“{cancel}”</p>}
    </Box>
  );
};

const DialsRow: React.FC<{ dials: Activity[] }> = ({ dials }) => {
  const [open, setOpen] = useState(false);
  const outcomes = Array.from(new Set(dials.map((d) => callFacts(d).connectionResult || 'no answer')));
  const word = outcomes.length === 1 ? outcomes[0].replace(/_/g, ' ') : 'unanswered';
  const single = dials.length === 1;
  return (
    <div className="grid grid-cols-[28px_minmax(0,1fr)] gap-2.5">
      <Dot><CallIcon className="w-3.5 h-3.5" /></Dot>
      <div className="border-[1.5px] border-dashed border-sketch rounded-lg px-3 py-1.5 text-[12.5px] text-graphite">
        <button type="button" className="w-full flex justify-between gap-2 text-left" disabled={single} onClick={() => setOpen(!open)}>
          <span>{single ? `Call · ${word}` : `${dials.length} attempts · ${word} ${open ? '▴' : '▾'}`}</span>
          <span className="whitespace-nowrap">{at(dials[0].occurred_at)}</span>
        </button>
        {open && (
          <div className="mt-1 space-y-0.5">
            {dials.map((d) => (
              <div key={d.id}>{(callFacts(d).connectionResult || 'no answer').replace(/_/g, ' ')} — {at(d.occurred_at)}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Timeline: React.FC<{ activity: Activity[]; tasks: Task[]; onOpenChat: () => void }> = ({ activity, tasks, onOpenChat }) => {
  const [filter, setFilter] = useState('all');
  const entries = useMemo(() => buildTimeline(activity, closureEntries(tasks)), [activity, tasks]);
  const shown = entries.filter(FILTERS.find(([k]) => k === filter)![2]);
  return (
    <div className="card">
      <h3 className="card-h"><Clock className="w-4 h-4" strokeWidth={1.75} />Timeline</h3>
      <div className="flex gap-1.5 flex-wrap mb-3">
        {FILTERS.map(([k, lbl]) => (
          <button key={k} type="button" className="pill" aria-pressed={filter === k} onClick={() => setFilter(k)}>{lbl}</button>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className="text-center py-10">
          <Inbox className="w-10 h-10 mx-auto text-graphite/50 mb-3" strokeWidth={1.25} />
          <p className="text-graphite m-0">{entries.length ? 'Nothing of this kind yet' : 'No activity yet'}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {shown.map((e) =>
            e.type === 'dials' ? <DialsRow key={e.key} dials={e.dials} />
              : e.type === 'closure' ? <ClosureRow key={e.key} task={e.closure.task} when={e.at} />
                : <ActivityRow key={e.key} a={e.activity} onOpenChat={onOpenChat} />)}
        </div>
      )}
    </div>
  );
};

export default Timeline;
